import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { env } from 'src/config/env'
import { paths } from 'src/config/paths'
import { HamburgerThinIcon } from 'src/shared/components/icons/hamburger-thin'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { Logo } from 'src/shared/components/icons/logos/logo'
import { BriefcaseFilled2 } from 'src/shared/components/icons/sidebar/briefcase-filled-2'
import { ConvertIcon } from 'src/shared/components/icons/sidebar/convert-icon'
import { HouseFilled2 } from 'src/shared/components/icons/sidebar/house-filled-2'
import { TwoPeopleIcon } from 'src/shared/components/icons/sidebar/two-people'
import { TwoRingIcon } from 'src/shared/components/icons/sidebar/two-ring'
import { WebpImage } from 'src/shared/components/ui/image/webp-image'
import IconDropdown from 'src/shared/components/ui/popups/dropdown-menu/icon-dropdown'
import { useWindowScrollBreakpoint } from 'src/shared/hooks/use-window-scroll-breakpoint'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import SolutionNavSideFallback from '../assets/nav/solution-nav-side.png'
import SolutionNavSideWebp from '../assets/nav/solution-nav-side.webp'
import { useProfessionalHeaderContext } from '../context/professional-header-context'
import { BlogIcon } from '../icons/header/blog'
import { HelpIcon } from '../icons/header/help'

import DownloadAppButton from './download-app-button'
import HeaderDropdown from './header-dropdown'
import ProfessionalNavSidebar from './professional-nav-sidebar'
import ScheduleDemoButton from './schedule-demo-button'

function SolutionsDropdown() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const getMoreBusinessOnClick = () => {
    navigate(paths.professionals.getMoreBusiness.getHref())
  }

  const implementAiOnClick = () => {
    navigate(paths.professionals.implementAi.getHref())
  }

  const convertMoreLeadsOnClick = () => {
    navigate(paths.professionals.convertMoreLeads.getHref())
  }

  const runYourBusinessOnClick = () => {
    navigate(paths.professionals.runYourBusiness.getHref())
  }

  const growYourNetworkOnClick = () => {
    navigate(paths.professionals.growYourNetwork.getHref())
  }

  return (
    <IconDropdown
      trigger={
        <button className="flex flex-row items-center gap-1 p-2">
          Solutions <ChevronDownIcon size={16} className={cn(open && 'rotate-180')} />
        </button>
      }
      customRightComponent={
        <div className="flex size-full w-[280px] flex-col items-center justify-between rounded-[8px] bg-[#F8F7F5] px-2 pb-[6px]">
          {/* <div className="h-[100px] w-full rounded-b-[20px] bg-[#D8D8D6]"></div> */}
          <WebpImage
            src={SolutionNavSideWebp}
            fallbackSrc={SolutionNavSideFallback}
            alt=""
            className="size-full object-cover"
            containerClassName="w-[220px] h-[210px]"
          />
          <div className="mb-4 max-w-[210px] pl-2">
            <div className="flex flex-row items-center gap-2">
              <p className="text-[20px] font-semibold leading-[1.2] tracking-[-0.3px] text-primary">Switch to</p>
              <Logo className="h-[20px] w-[100px]" showCircleR={false} />
            </div>
            <p className="mt-2 text-[14px] leading-[1.4] tracking-[-0.21px] text-muted">
              {"Leave your old tools behind. We'll handle the rest."}
            </p>
          </div>
        </div>
      }
      alignContent="start"
      onOpenChange={setOpen}
      dropdownItemClassName="min-w-[352px]"
      itemGrid={[
        [
          {
            label: 'Get more business',
            subLabel: 'New clients find you. AI does the warming up.',
            onClick: getMoreBusinessOnClick,
            icon: <BriefcaseFilled2 size={20} />,
          },
          {
            label: 'Implement AI into your work',
            subLabel: 'Meet Lynk. Your AI employee',
            onClick: implementAiOnClick,
            icon: <TwoRingIcon size={20} />,
          },
          {
            label: 'Convert more leads',
            subLabel: 'Plug in your lead sources. Lynk works them all.',
            onClick: convertMoreLeadsOnClick,
            icon: <ConvertIcon size={24} />,
          },
          {
            label: 'Run your business',
            subLabel: 'One system for deals, clients, and partners.',
            onClick: runYourBusinessOnClick,
            icon: <HouseFilled2 size={21} />,
          },
          {
            label: 'Grow your network',
            subLabel: 'Your network is your net worth. Grow it.',
            onClick: growYourNetworkOnClick,
            icon: <TwoPeopleIcon size={22} />,
          },
        ],
      ]}
    />
  )
}

function ResourcesDropdown() {
  const [open, setOpen] = useState(false)

  // const integrationsOnClick = () => {
  //   navigate(paths.professionals.integrations.getHref())
  // }

  const blogOnClick = () => {
    window.open(paths.blog.list.getHref(), '_blank')
  }

  const helpCenterOnClick = () => {
    window.open(env.LINK_TO_HELP_CENTER, '_blank')
  }

  return (
    <IconDropdown
      trigger={
        <button className="flex flex-row items-center gap-1 p-2">
          Resources <ChevronDownIcon size={16} className={cn(open && 'rotate-180')} />
        </button>
      }
      alignContent="start"
      dropdownItemClassName="min-w-[304px]"
      onOpenChange={setOpen}
      itemGrid={[
        [
          // {
          //   label: 'Integrations',
          //   subLabel: 'Connect your existing tools.',
          //   onClick: integrationsOnClick,
          //   icon: <IntegrationsIcon />,
          // },
          {
            label: 'Blog',
            subLabel: 'Insights and updates',
            onClick: blogOnClick,
            icon: <BlogIcon />,
          },
          {
            label: 'Help center',
            subLabel: 'Guides and answers.',
            onClick: helpCenterOnClick,
            icon: <HelpIcon />,
          },
        ],
      ]}
    />
  )
}

interface ProfessionalHeaderProps {
  className?: string
  hideScheduleDemoButton?: boolean
}

export default function ProfessionalHeader({ className, hideScheduleDemoButton = false }: ProfessionalHeaderProps) {
  const { theme } = useProfessionalHeaderContext()
  const { width } = useWindowSize()
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()

  const overviewOnClick = () => {
    navigate(paths.professionals.overview.getHref())
  }

  // const membershipOnClick = () => {
  //   console.log('membershipOnClick')
  //   // navigate(paths.enterprise.solutions.pricing.getHref())
  // }

  const showShadow = useWindowScrollBreakpoint({ breakpoint: 5 })
  const shouldDecreaseGap = width < 1100
  const shouldHideTabs = width < 1020

  return (
    <>
      <header
        className={cn(
          'fixed top-0 z-50 flex w-full flex-col items-center justify-between bg-white',
          // 'transition-colors duration-200',
          theme === 'light' && 'bg-transparent',
          theme === 'dark' && showShadow && 'border-b-[0.5px] border-b-[#EFEFEF]',
          className,
        )}
      >
        <div className="mx-auto flex h-[65px] w-full items-center justify-center overflow-x-clip p-4 md:h-[70px] md:px-8">
          <div className={cn('mx-auto flex w-full max-w-[1250px] items-center justify-between')}>
            <div className={cn('flex flex-row items-center gap-10', shouldDecreaseGap && 'gap-5')}>
              <div className="flex flex-row items-center gap-3 sm:gap-6">
                <Link to={paths.root.getHref()} className="flex h-full flex-col items-start justify-center gap-2">
                  {width < 330 ? (
                    <Iconmark
                      className={cn('size-[30px] text-primary md:size-[32px]', theme === 'light' && 'text-white')}
                    />
                  ) : (
                    <Logo
                      className={cn(
                        'h-[25px] w-[121px] text-primary md:h-[25px] md:w-[121px]',
                        theme === 'light' && 'text-white',
                      )}
                      showCircleR={false}
                    />
                  )}
                </Link>
              </div>
              {!shouldHideTabs && (
                <div className="flex flex-row items-center">
                  <HeaderDropdown theme={theme} title="For agents & LOs" />
                  <div
                    className={cn('mx-5 h-[30px] w-[1px] flex-shrink-0 bg-[#D8D8D6]', shouldDecreaseGap && 'mx-2')}
                  ></div>
                  <div
                    className={cn(
                      'flex flex-row items-center gap-[34px] text-[15px] tracking-[-0.15px] text-primary',
                      theme === 'light' && 'text-white',
                      shouldDecreaseGap && 'gap-4',
                    )}
                  >
                    <button className="p-2" onClick={overviewOnClick}>
                      Overview
                    </button>
                    <SolutionsDropdown />
                    {/* <button className="p-2 font-semibold" onClick={membershipOnClick}>
                      Membership
                    </button> */}
                    <ResourcesDropdown />
                  </div>
                </div>
              )}
            </div>
            <div className="relative flex items-center justify-end">
              <div className={cn('flex flex-row items-center gap-3')}>
                {!hideScheduleDemoButton && <ScheduleDemoButton theme={theme} variant="professional" />}
                {width > 420 && <DownloadAppButton theme={theme} />}
                {shouldHideTabs && (
                  <button onClick={() => setIsOpen(true)} className="py-3 pl-1">
                    <HamburgerThinIcon className={cn('text-primary', theme === 'light' && 'text-white')} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <ProfessionalNavSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  )
}
